import React, { FC, ReactNode } from "react";

import { useSubscriptionsProviderContext, useSubscriptionEditProviderContext, SubscriptionEditProvider } from "../../providers";
import { SubscriptionItemType } from "../../types";

import { SubscriptionsSettingsActions } from "./SubscriptionsSettingsActions";
import { SubscriptionsSettingsSchedule } from "./SubscriptionsSettingsSchedule";
import { SubscriptionsSettingsName } from "./SubscriptionsSettingsName";
import { SubscriptionsSettingsDate } from "./SubscriptionsSettingsDate";
import { SubscriptionsSettingsFileFormat } from "./SubscriptionsSettingsFileFormat";
import { SubscriptionsSettingsSection } from "./SubscriptionsSettingsSection";

import { SubscriptionsSettingsBulkExport } from "./SubscriptionsSettingsBulkExport";
import { SubscriptionsSettingsSlide } from "./SubscriptionsSettingsSlide";

import { useStyles } from "./SubscriptionsSettings.styles";
import { messages } from "./SubscriptionsSettings.messages";
import { SubscriptionsSourceNameBulkExport } from "./SubscriptionsSettingsBulkExport/SubscriptionsSourceNameBulkExport/SubscriptionsSourceNameBulkExport";

const SubscriptionsSettingsContent: FC = () => {
  const classes = useStyles();
  const {
    state: { currentSubscriptionItem, savedSubscriptionItem, validationErrors },
    actions: { setSubscriptionPropertyValue, setApplyPayloadFunction },
  } = useSubscriptionEditProviderContext();
  let settings: ReactNode = null;
  switch (currentSubscriptionItem.type) {
    case SubscriptionItemType.Slide:
      settings = (
        <>
          <SubscriptionsSettingsSection label={messages.slideName} horizontal>
            {currentSubscriptionItem.payload.title}
          </SubscriptionsSettingsSection>
          <SubscriptionsSettingsSection label={messages.selections} className={classes.slideSelection}>
            <SubscriptionsSettingsSlide
              value={currentSubscriptionItem.payload}
              onChange={setSubscriptionPropertyValue("payload")}
              setApplyPayloadFunction={setApplyPayloadFunction}
            />
          </SubscriptionsSettingsSection>
        </>
      );
      break;
    case SubscriptionItemType.BulkExport:
      settings = (
        <>
          <SubscriptionsSettingsSection label={messages.bulkExportSource} horizontal>
            <SubscriptionsSourceNameBulkExport />
          </SubscriptionsSettingsSection>
          <SubscriptionsSettingsSection label={messages.selections}>
            <SubscriptionsSettingsBulkExport
              value={currentSubscriptionItem.payload}
              onChange={setSubscriptionPropertyValue("payload")}
              setApplyPayloadFunction={setApplyPayloadFunction}
            />
          </SubscriptionsSettingsSection>
        </>
      );
      break;
    case SubscriptionItemType.SavedBulkExport:
      settings = (
        <>
          <SubscriptionsSettingsSection label={messages.savedBulkExport} horizontal>
            <SubscriptionsSourceNameBulkExport />
          </SubscriptionsSettingsSection>
        </>
      );
      break;
    default:
      settings = null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.settingsContainer}>
        <SubscriptionsSettingsName
          value={currentSubscriptionItem.name}
          error={validationErrors?.name}
          onChange={setSubscriptionPropertyValue("name")}
        />
        {savedSubscriptionItem && <SubscriptionsSettingsDate value={savedSubscriptionItem.date} />}

        {settings}
        <SubscriptionsSettingsSection label={messages.fileFormat}>
          <SubscriptionsSettingsFileFormat
            value={currentSubscriptionItem.fileFormat}
            type={currentSubscriptionItem.type}
            onChange={setSubscriptionPropertyValue("fileFormat")}
          />
        </SubscriptionsSettingsSection>

        <SubscriptionsSettingsSection label={messages.frequencyParameters}>
          <SubscriptionsSettingsSchedule value={currentSubscriptionItem.schedule} onChange={setSubscriptionPropertyValue("schedule")} />
        </SubscriptionsSettingsSection>
      </div>
      <SubscriptionsSettingsActions />
    </div>
  );
};

export const SubscriptionsSettings: FC = () => {
  const {
    state: { selectedSubscriptionItem },
  } = useSubscriptionsProviderContext();

  if (!selectedSubscriptionItem) {
    return null;
  }

  return (
    <SubscriptionEditProvider key={selectedSubscriptionItem.id} subscriptionItem={selectedSubscriptionItem}>
      <SubscriptionsSettingsContent />
    </SubscriptionEditProvider>
  );
};
